import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getPhotoUrl } from '@/utils/photoUrl';
import { 
  Trash2, User, Link2, Unlink, UserCheck, Edit
} from "lucide-react";

const ROLES = [
  { value: "admin", label: "Administrador" },
  { value: "tecnico", label: "Tecnico" },
  { value: "avaliador", label: "Avaliador" },
];

function getPlayerName(p) {
  return p?.first_name ? `${p.first_name} ${p.last_name}` : p?.name || '';
}

function EvaluatorAvatar({ linkedPlayer, linkedPhotoUrl }) {
  if (!linkedPlayer) return null;
  return (
    <div className="relative flex-shrink-0">
      <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-green-500/50 bg-slate-800">
        {linkedPhotoUrl ? (
          <img src={linkedPhotoUrl} alt={getPlayerName(linkedPlayer)} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <User className="h-6 w-6 text-slate-500" />
          </div>
        )}
      </div>
      <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center border-2 border-background">
        <Link2 className="h-3 w-3 text-white" />
      </div>
    </div>
  );
}

function EvaluatorInfo({ eval_, isSelf, linkedPlayer }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium truncate">{eval_.name}</span>
        {isSelf && <Badge variant="outline" className="text-xs">Voce</Badge>}
      </div>
      <p className="text-sm text-muted-foreground truncate">{eval_.email}</p>
      {linkedPlayer && (
        <div className="flex items-center gap-2 mt-1 p-1.5 bg-green-500/10 rounded-md">
          <UserCheck className="h-3.5 w-3.5 text-green-500" />
          <span className="text-xs text-green-400 font-medium">Vinculado: {getPlayerName(linkedPlayer)}</span>
          {linkedPlayer.position && (
            <Badge variant="outline" className="text-[10px] border-green-500/30 text-green-400">{linkedPlayer.position}</Badge>
          )}
        </div>
      )}
    </div>
  );
}

function EvaluatorActions({ eval_, isAdmin, isSelf, onChangeRole, onLinkPlayer, onUnlinkPlayer, onEdit, onDelete }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {isAdmin && !isSelf ? (
        <Select value={eval_.role || 'avaliador'} onValueChange={(v) => onChangeRole(eval_.id, v)}>
          <SelectTrigger className="w-32 h-9 text-xs bg-input/50"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLES.map(role => (<SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>))}
          </SelectContent>
        </Select>
      ) : (
        <Badge variant={eval_.role === 'admin' ? 'default' : 'secondary'} className="capitalize">{eval_.role || 'avaliador'}</Badge>
      )}

      {isAdmin && !eval_.linked_player_id && (
        <Button variant="outline" size="sm" onClick={() => onLinkPlayer(eval_)} className="h-9 text-xs" data-testid={`link-player-btn`}>
          <Link2 className="h-3 w-3 mr-1" /> Vincular Jogador
        </Button>
      )}

      {isAdmin && eval_.linked_player_id && (
        <Button variant="outline" size="sm" onClick={() => onUnlinkPlayer(eval_.id, eval_.name)}
          className="h-9 text-xs border-orange-500/50 text-orange-500 hover:bg-orange-500/10 hover:text-orange-400">
          <Unlink className="h-3 w-3 mr-1" /> Desvincular
        </Button>
      )}

      {isAdmin && (
        <Button variant="outline" size="sm" onClick={() => onEdit(eval_)}
          className="h-9 text-xs border-blue-500/50 text-blue-500 hover:bg-blue-500/10 hover:text-blue-400">
          <Edit className="h-3 w-3 mr-1" /> Editar
        </Button>
      )}

      {isAdmin && !isSelf && (
        <Button variant="ghost" size="sm"
          className="text-destructive hover:text-destructive hover:bg-destructive/10 h-9"
          onClick={() => onDelete(eval_.id, eval_.name)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export function EvaluatorCard({ 
  eval_, index, evaluator, players,
  selectedEvaluators, onToggleSelection, onToggleActive, 
  onChangeRole, onDelete, onLinkPlayer, onUnlinkPlayer, onEdit 
}) {
  const linkedPlayer = players.find(p => p.id === eval_.linked_player_id) || null;
  const linkedPhotoUrl = linkedPlayer?.photo_url ? getPhotoUrl(linkedPlayer.photo_url) : null;
  const isAdmin = evaluator?.role === 'admin';
  const isSelf = eval_.id === evaluator.id;

  return (
    <div 
      className={`p-4 rounded-lg border transition-all ${
        selectedEvaluators.includes(eval_.id) 
          ? 'bg-primary/10 border-primary/50' 
          : eval_.linked_player_id
            ? 'bg-green-500/5 border-green-500/30 hover:border-green-500/50'
            : 'bg-card/50 border-border/50 hover:border-border'
      }`}
      data-testid={`evaluator-row-${index}`}
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          {isAdmin && (
            <Checkbox
              checked={selectedEvaluators.includes(eval_.id)}
              onCheckedChange={() => onToggleSelection(eval_.id)}
              disabled={isSelf}
              data-testid={`select-evaluator-${index}`}
              className="mt-1"
            />
          )}
          <EvaluatorAvatar linkedPlayer={linkedPlayer} linkedPhotoUrl={linkedPhotoUrl} />
          <EvaluatorInfo eval_={eval_} isSelf={isSelf} linkedPlayer={linkedPlayer} />
          <Switch
            checked={eval_.is_active !== false}
            onCheckedChange={() => onToggleActive(eval_.id, eval_.is_active !== false)}
            disabled={!isAdmin}
            data-testid={`toggle-active-${index}`}
          />
        </div>
        <EvaluatorActions
          eval_={eval_}
          isAdmin={isAdmin}
          isSelf={isSelf}
          onChangeRole={onChangeRole}
          onLinkPlayer={onLinkPlayer}
          onUnlinkPlayer={onUnlinkPlayer}
          onEdit={onEdit}
          onDelete={onDelete}
        />
      </div>
    </div>
  );
}
